import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Trophy, Star, Award } from 'lucide-react';
import { getApiUrl } from '@/lib/utils';

const API_URL = getApiUrl();

export function MatchResultModal({ match, isOpen, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState([]);
  const [formData, setFormData] = useState({
    team_a_score: '',
    team_b_score: '',
    winner: '',
    man_of_match: '',
    highlights: ''
  });

  useEffect(() => {
    if (isOpen && match) {
      // Pre-fill with existing result if any
      const result = match.result || {};
      setFormData({
        team_a_score: result.team_a_score || '',
        team_b_score: result.team_b_score || '',
        winner: result.winner || '',
        man_of_match: result.man_of_match || '',
        highlights: result.highlights || ''
      });
      
      // Get selected players for Man of the Match dropdown
      fetchSelectedPlayers();
    }
  }, [isOpen, match]);

  const fetchSelectedPlayers = async () => {
    if (!match?.selected_players?.length) return;
    
    try {
      // Fetch match details to get player info from RSVPs
      const response = await axios.get(`${API_URL}/api/matches/${match.match_id}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      
      // Extract players from RSVPs who were selected
      const selectedPlayerIds = new Set(match.selected_players);
      const matchPlayers = response.data.rsvps
        ?.filter(r => selectedPlayerIds.has(r.user_id))
        ?.map(r => r.user) || [];
      
      setPlayers(matchPlayers);
    } catch (error) {
      console.error('Failed to fetch players:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {};
      if (formData.team_a_score) payload.team_a_score = formData.team_a_score;
      if (formData.team_b_score) payload.team_b_score = formData.team_b_score;
      if (formData.winner) payload.winner = formData.winner;
      if (formData.man_of_match) payload.man_of_match = formData.man_of_match;
      if (formData.highlights) payload.highlights = formData.highlights;
      
      await axios.put(
        `${API_URL}/api/matches/${match.match_id}/result`,
        payload,
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      
      toast.success('Match result saved!');
      onSave?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save result');
    } finally {
      setLoading(false);
    }
  };

  const getTeamLabel = (team) => {
    if (match?.match_type === 'sunday') {
      return team === 'a' ? 'Team A (Bhandball)' : 'Team B (Kacche Khiladi)';
    }
    return team === 'a' ? 'Weakenders' : match?.opponent || 'Opponent';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#E63946]" />
            Record Match Result
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Scores */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="team_a_score">{getTeamLabel('a')} Score</Label>
              <Input
                id="team_a_score"
                placeholder="e.g., 156/8 (20 overs)"
                value={formData.team_a_score}
                onChange={(e) => setFormData({ ...formData, team_a_score: e.target.value })}
                data-testid="team-a-score-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team_b_score">{getTeamLabel('b')} Score</Label>
              <Input
                id="team_b_score"
                placeholder="e.g., 142/10 (18.4 overs)"
                value={formData.team_b_score}
                onChange={(e) => setFormData({ ...formData, team_b_score: e.target.value })}
                data-testid="team-b-score-input"
              />
            </div>
          </div>
          
          {/* Winner */}
          <div className="space-y-2">
            <Label htmlFor="winner">Winner</Label>
            <Select
              value={formData.winner}
              onValueChange={(value) => setFormData({ ...formData, winner: value })}
            >
              <SelectTrigger data-testid="winner-select">
                <SelectValue placeholder="Select winner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="team_a">{getTeamLabel('a')}</SelectItem>
                <SelectItem value="team_b">{getTeamLabel('b')}</SelectItem>
                <SelectItem value="tie">Tie / Draw</SelectItem>
                <SelectItem value="no_result">No Result</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Man of the Match */}
          <div className="space-y-2">
            <Label htmlFor="man_of_match" className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-500" />
              Man of the Match
            </Label>
            <Select
              value={formData.man_of_match}
              onValueChange={(value) => setFormData({ ...formData, man_of_match: value })}
            >
              <SelectTrigger data-testid="mom-select">
                <SelectValue placeholder="Select MVP" />
              </SelectTrigger>
              <SelectContent>
                {players.map((player) => (
                  <SelectItem key={player.user_id} value={player.user_id}>
                    {player.nickname || player.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Highlights */}
          <div className="space-y-2">
            <Label htmlFor="highlights">Match Highlights</Label>
            <Textarea
              id="highlights"
              placeholder="Brief summary of the match..."
              value={formData.highlights}
              onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
              rows={3}
              data-testid="highlights-input"
            />
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#E63946] hover:bg-[#DC2626]"
              data-testid="save-result-btn"
            >
              {loading ? 'Saving...' : 'Save Result'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default MatchResultModal;
